import type { ReactNode } from "react";
import { RouterLink as Link } from "../../router";
import { DataTable, type DataTableColumn } from "../data-table";
import { IconTooltip } from "../icon-tooltip";
import { StatusBadge } from "../status-badge";
import { formatUsd, formatDateTime, roleLabel, agentDisplayName } from "../../format";
import { classifyRunExit, type WorkerRunStatus } from "../../tokens";
import type { RunTraceStep } from "../optimization/run-trace";

export interface WorkerRunItem {
  id: number;
  role: string;
  goal_id: number | null;
  goal_title: string | null;
  agent_id: string;
  status: WorkerRunStatus;
  exit_reason: string | null;
  started_at: string;
  finished_at: string | null;
  tasks_claimed: number;
  tasks_shipped: number;
  tasks_verified: number;
  // v38 telemetry (Optimization Harness Phase 1) — optional so existing
  // consumers (goal detail, now page) compile without supplying them.
  loop_id?: number | null;
  variant_id?: number | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  cached_tokens?: number | null;
  cost_usd?: number | null;
  /** Vendor vs unknown provenance. Unknown numeric cost must not render as dollars. */
  cost_source?: string | null;
  wall_ms?: number | null;
  model?: string | null;
  outcome?: string | null;
  // Per-step trace for the RunTrace drawer (only the /agents feed populates it).
  steps?: RunTraceStep[];
}

interface WorkerRunsTableProps {
  runs: WorkerRunItem[];
  /** Row click → open the RunTrace drawer (the /agents feed wires this). */
  onRowClick?: (run: WorkerRunItem) => void;
  /** Hide telemetry columns where they'd be noise (e.g. goal detail). */
  showTelemetry?: boolean;
  /** Base path goal links resolve against. Default `/agents/goals` (the Agents
   *  panel's Goals tab); the operations section passes
   *  `/projects/<slug>/operations/goals`. */
  basePath?: string;
  // Integrated card-header passthrough (forwarded to DataTable). Set `title`
  // to render the flush "Recent runs" header — the SectionHeader chrome the
  // Recent-activity ListCard uses — instead of hand-rolling a
  // `<Card><CardHeader>…<CardContent>` wrapper around the table at each call
  // site. `headerAction` carries the "Run a worker" / "All runs →" controls.
  title?: ReactNode;
  subtitle?: ReactNode;
  count?: number;
  headerAction?: ReactNode;
  footer?: ReactNode;
  /** Caption band rendered flush above the column header (e.g. a
   *  `<StatStrip variant="table">`), making the strip the table's own header. */
  summary?: ReactNode;
  /** Override the empty-body copy (defaults to "No worker runs recorded yet."). */
  empty?: ReactNode;
  /** Pass false to render the bare table inside an existing Card. Defaults to
   *  the DataTable behaviour (wraps in a flush Card). */
  wrapInCard?: boolean;
  className?: string;
}

function fmtTokens(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtMs(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${n}ms`;
}

// Strip the "[1m]" / context suffix and provider prefix for a compact label.
function shortModel(model: string | null | undefined): string {
  if (!model) return "—";
  return model.split(",")[0]!.replace(/\[.*?\]$/, "");
}

export function WorkerRunsTable({
  runs,
  onRowClick,
  showTelemetry = true,
  basePath = "/agents/goals",
  title,
  subtitle,
  count,
  headerAction,
  footer,
  summary,
  empty,
  wrapInCard,
  className,
}: WorkerRunsTableProps) {
  const columns: DataTableColumn<WorkerRunItem>[] = [
    {
      key: "id",
      header: "run",
      cellClassName: "text-muted-foreground",
      render: (r) => `#${r.id}`,
    },
    {
      key: "role",
      header: "role",
      render: (r) => roleLabel(r.role),
    },
    {
      key: "agent",
      header: "agent",
      cellClassName: "type-subtext text-muted-foreground",
      render: (r) => agentDisplayName(r.agent_id),
    },
    {
      key: "goal",
      header: "goal",
      render: (r) =>
        r.goal_id ? (
          <Link
            href={`${basePath}/${r.goal_id}`}
            className="text-foreground hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            #{r.goal_id} {r.goal_title}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "status",
      header: "status",
      align: "center",
      cellClassName: "px-2 py-2",
      // The exit reason used to hang off a native `title=` — unstyled, slow to
      // appear, and invisible on touch, against the standing rule that every
      // hover explanation is an `IconTooltip`. It also showed the raw marker
      // and nothing else; now the bucket says what it MEANS, with the marker
      // kept underneath so the badge stays checkable against the ledger.
      render: (r) => {
        const badge = <StatusBadge domain="worker_run" status={r.status} />;
        if (!r.exit_reason) return badge;
        const bucket = classifyRunExit(r.exit_reason);
        return (
          <IconTooltip
            label={
              <span className="flex max-w-72 flex-col gap-1">
                <span>
                  {bucket.label} — {bucket.hint}
                </span>
                <span className="font-mono type-subtext break-all opacity-70">{r.exit_reason}</span>
              </span>
            }
          >
            <span className="inline-flex cursor-default">{badge}</span>
          </IconTooltip>
        );
      },
    },
  ];

  if (showTelemetry) {
    columns.push(
      {
        key: "model",
        header: "model",
        cellClassName: "font-mono type-subtext text-muted-foreground",
        render: (r) => shortModel(r.model),
      },
      {
        key: "tokens",
        header: "tokens",
        cellClassName: "type-subtext tabular-nums",
        render: (r) => (
          <span>
            {fmtTokens(r.input_tokens)}/{fmtTokens(r.output_tokens)}
            {r.cached_tokens ? (
              <span className="text-muted-foreground">
                {" "}
                ({fmtTokens(r.cached_tokens)}c)
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: "cost",
        header: "cost",
        cellClassName: "type-subtext tabular-nums",
        render: (r) =>
          r.cost_source === "unknown" ? "Not claimable" : formatUsd(r.cost_usd),
      },
      {
        key: "latency",
        header: "latency",
        cellClassName: "type-subtext tabular-nums text-muted-foreground",
        render: (r) => fmtMs(r.wall_ms),
      },
      {
        key: "outcome",
        header: "outcome",
        align: "center",
        render: (r) =>
          r.outcome ? (
            <span
              className={
                r.outcome === "pass"
                  ? "text-success"
                  : r.outcome === "fail"
                    ? "text-destructive"
                    : "text-muted-foreground"
              }
            >
              {r.outcome}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    );
  }

  columns.push(
    {
      key: "counts",
      header: "counts",
      cellClassName: "type-subtext text-muted-foreground",
      render: (r) => `c:${r.tasks_claimed} s:${r.tasks_shipped} v:${r.tasks_verified}`,
    },
    {
      key: "started",
      header: "started",
      cellClassName: "type-subtext text-muted-foreground",
      render: (r) => formatDateTime(r.started_at),
    },
  );

  return (
    <DataTable
      rows={runs}
      columns={columns}
      rowKey={(r) => r.id}
      onRowClick={onRowClick}
      empty={empty ?? "No worker runs recorded yet."}
      title={title}
      subtitle={subtitle}
      count={count}
      headerAction={headerAction}
      footer={footer}
      summary={summary}
      wrapInCard={wrapInCard}
      className={className}
    />
  );
}
